/**
 * training-menu.js
 */

import React from 'react';
import {
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';
import { Types } from 'utils/constants';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { makeLocation, paths } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import RouteLink from 'routing/route-link';
import DrawerMenu from 'components/menu-drawer/menu';
import DrawerMenuItem from 'components/menu-drawer/menu/drawer-menu-item';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import EventMenuItem from '../event/menu-item';
import ExerciseMenuItem from '../exercise/menu-item';
import ExerciseHandlers from '../exercise/menu-item/exercise-handlers';
import AppBar from 'components/app-bar';
import Icon from 'components/icon';
import Button from 'components/button';
import ScrollableList from 'components/scrollable-list';
import { ReactComponent as EventSVG } from 'assets/images/event.svg';
import TrainingWizardStepper from './training-wizard-stepper';
import TrainingWizardComplete from './training-wizard-complete';


export default function TrainingMenu(props) {
  const {
    locationOverride,
    isActiveOverride,
    extraExercises,
    onClose,
    wizard,
    params,
    basePath,
    showAddExercise,
    drawerState,
  } = props;
  let location = useLocation();
  const { action, type, id } = params || {};
  const { user } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const item = recordStore.useRecord(Types.Record.getId(type), id).ornull;
  const eventItem = recordStore.useRecord(Types.Record.EVENT, item ? item.get().eventId : 0);
  const eventRecord = type === 'event' ? item : eventItem;
  const hasEvent = Boolean(eventRecord?.get()?.id !== undefined);
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), {noSsr: true});
  const context = isMobileDrawer ? 'content' : 'menu';
  const [menuGroups, setMenuGroups] = React.useState();
  const isLeader = Boolean(eventRecord && eventRecord.get()?.membership?.isLeader);


  // loading complete
  if (eventRecord && drawerState.loading[context].get()) {
    drawerState.loading[context].set(false);
  }


  // generates a menu item
  const generateMenuItem = React.useCallback((type, id, locationParams, forceActive) => {
    let locationObj = locationOverride && locationOverride(type, id);
    if (!locationObj && locationParams) {
      locationObj = makeLocation(
        basePath,
        'training',
        locationParams.pathItemName,
        locationParams.pathParams,
        location
      );
    }

    const isActive = forceActive !== undefined
      ? forceActive
      : isActiveOverride
        ? isActiveOverride(type, id, locationObj)
        : location.pathname.startsWith(locationObj.pathname);

    const isHighlighted = !locationObj || location.pathname.startsWith(locationObj.pathname);

    const itemProps = {
      id,
      key: id,
      to: locationObj,
      isActive,
      isHighlighted,
    };
    if (type === Types.Record.EVENT) {
      return <EventMenuItem {...itemProps} />;
    } else if (type === Types.Record.EXERCISE) {
      return <ExerciseMenuItem {...itemProps} basePath={basePath} />;
    }
  }, [location, basePath, locationOverride, isActiveOverride]);


  // build menu groups
  React.useEffect(() => {
    if (hasEvent) {
      const isWizardEvent = Boolean(action === 'create' && type === 'event' && id === 0);
      const isEditEvent = Boolean(action === 'edit' && type === 'event');

      setMenuGroups([

        // EVENT header
        {
          name: 'Event',
          items: generateMenuItem(
            Types.Record.EVENT,
            eventRecord.get().id, 
            isWizardEvent
              ? null
              : {
                pathItemName: 'viewEvent',
                pathParams: { eventId: eventRecord.get().id }
              },
            isWizardEvent || isEditEvent ? true : undefined
          )
        }, 
        
        // EXERCISES list
        {
          name: 'Exercises',
          scrollable: true,
          component: ScrollableList,
          items: (
            <>

              {/* custom extras in props */}
              {extraExercises}

              {/* add exercise action */}
              {action === 'add-exercise' &&
              <ExerciseMenuItem
                id={0}
                to={makeLocation(
                  basePath,
                  'training',
                  'addExercise',
                  {eventId: eventRecord.get().id},
                  location
                )}
                isActive={true}
              />}


              {/* exercise menu items */}
              {eventRecord.get()?.exercises?.map(exerciseId => {
                const matchEditAnswer = matchPath(location.pathname, `${basePath}${paths.training.base}${paths.training.editAnswer.path}`);
                const matchViewHandler = matchPath(location.pathname, `${basePath}${paths.training.base}${paths.training.viewHandlerAnswers.path}`);
                const isActive = Boolean(matchViewHandler)
                  ? false
                  : type === 'exercise' && id === exerciseId
                    ? true
                    : undefined;
                const exerMenuItem = generateMenuItem(
                  Types.Record.EXERCISE,
                  exerciseId, {
                    pathItemName: action === 'edit' && type === 'exercise' && id === exerciseId && !matchEditAnswer
                      ? 'editExercise'
                      : 'viewExercise',
                    pathParams: {exerciseId}
                  },
                  isActive
                );

                // super/trainer handler list
                const handlerMenuItems = ((user.role.isSupervisor || user.role.isTrainer) && !isMobileDrawer)
                  ? <ExerciseHandlers id={exerciseId} basePath={basePath} params={params} />
                  : null;

                return <span key={exerciseId}>
                  {exerMenuItem}
                  {handlerMenuItems}
                </span>;
              })}


              {/* none item (no exercises) */}
              {eventRecord.get().exercises?.length === 0 && !extraExercises && action !== 'add-exercise' &&
              <DrawerMenuItem
                id="none"
                iconName="not-applicable"
                label="None"
                disabled={true}
              />}
            </>
          )
        }

      ])
    }

  // eventRecord is complex hookstate object. action/type/id trigger this when eventRecord changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    action, 
    type, 
    id, 
    hasEvent, 
    basePath, 
    extraExercises, 
    params, 
    location, 
    generateMenuItem,
    user.role.isSupervisor,
    user.role.isTrainer
  ]);


  const closeButton = (wizard
    ? <DrawerActionButton
        level={1}
        onClick={onClose}
      >
        Done
      </DrawerActionButton>
    : <IconButton
        data-testid="mobile-drawer-close-button"
        size="small"
        style={{color: 'inherit'}}
        onClick={onClose}
      >
        <Icon type='mobile-close-drawer' />
      </IconButton>);


  return (
    <>
      {menuGroups &&
      <DrawerMenu
        menuGroups={menuGroups}
        header={<>
          {shouldRenderMobileLayout() &&
          <AppBar
            title={wizard ? 'Review' : 'Training'}
            rightActions={closeButton}
            recordGroupType={Types.RecordGroup.TRAINING}
          />}

          {wizard &&
          <TrainingWizardStepper
            basePath={basePath}
            paddingTop={3}
            paddingBottom={1.5}
          />}

          {shouldRenderMobileLayout() &&
          <TrainingWizardComplete basePath={basePath} />}
        </>}
        footer={<>
          <EventSVG />
          {(showAddExercise === undefined || showAddExercise !== false) 
            && (isLeader || user.role.isTrainer)
            && <Button
            component={RouteLink}
            level={1}
            aria-label="close drawer"
            startIcon={<Icon type='add' />}
            // route params
            basePath={basePath}
            pathSectionName='training'
            pathItemName='addExercise'
            pathParams={{ eventId: eventRecord.get().id }}
            disabled={!user.role.status.isActive}
          >
            Exercise
          </Button>}
        </>}
      />}
    </>
  );
}